import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { Logo } from '../components/Logo'
import { apiUrl } from '../lib/apiBase'
import { t } from '../i18n'

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [error, setError] = useState<string | null>(null)
  const requested = useRef(false)

  useEffect(() => {
    if (!token || requested.current) return
    // React StrictMode double-invokes effects; the token is single-use, so
    // guard against firing the request twice.
    requested.current = true

    const accept = async () => {
      try {
        const response = await fetch(apiUrl('/auth/accept_invite'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
        const data = await response.json()

        if (response.ok) {
          login(
            data.user,
            data.token,
            data.memberships || [],
            data.current_membership,
            data.requires_membership_selection
          )
          navigate('/', { replace: true })
        } else {
          setError(data.error || t('invite.errGeneric'))
        }
      } catch {
        setError(t('invite.errGeneric'))
      }
    }
    accept()
  }, [token, login, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        {error ? (
          <div className="bg-white rounded-xl shadow p-8">
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <Link
              to="/login"
              className="inline-block rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t('invite.goToLogin')}
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center gap-4">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <p className="text-sm text-gray-600">{t('invite.accepting')}</p>
          </div>
        )}
      </div>
    </div>
  )
}
